// Admin data access. Runs in the browser with the authenticated Supabase session;
// RLS policies (is_admin()) enforce authorization server-side in the database, so
// no Node/SSR server functions are needed and the app can be hosted statically.
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

export type Row = Record<string, any>;

// Whitelisted, manageable tables with their id column + default ordering.
const MANAGE_TABLES = {
  proposal_categories: { idColumn: "id", order: [["sort_order", true]] },
  work_categories: { idColumn: "id", order: [["sort_order", true]] },
  news_categories: { idColumn: "id", order: [["sort_order", true]] },
  proposals: { idColumn: "id", order: [["sort_order", true], ["created_at", false]] },
  works: { idColumn: "id", order: [["sort_order", true], ["created_at", false]] },
  news: { idColumn: "id", order: [["created_at", false]] },
  events: { idColumn: "id", order: [["event_date", false]] },
  videos: { idColumn: "id", order: [["sort_order", true]] },
  galleries: { idColumn: "id", order: [["sort_order", true]] },
  gallery_images: { idColumn: "id", order: [["sort_order", true]] },
  testimonials: { idColumn: "id", order: [["sort_order", true]] },
  timeline: { idColumn: "id", order: [["sort_order", true]] },
  numbers: { idColumn: "id", order: [["sort_order", true]] },
  materials: { idColumn: "id", order: [["sort_order", true], ["created_at", false]] },
  pages: { idColumn: "slug", order: [["slug", true]] },
  contacts: { idColumn: "id", order: [["created_at", false]] },
  participation_forms: { idColumn: "id", order: [["created_at", false]] },
} as const;

type ManageTable = keyof typeof MANAGE_TABLES;
const TABLE_NAMES = Object.keys(MANAGE_TABLES) as [ManageTable, ...ManageTable[]];

async function requireUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) throw new Error("Unauthorized");
  return data.user;
}

export async function getMe() {
  const user = await requireUser();
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", user.id)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  return {
    userId: user.id,
    email: user.email ?? null,
    isAdmin: !!data,
  };
}

const listSchema = z.object({
  table: z.enum(TABLE_NAMES),
  limit: z.number().int().positive().max(500).optional(),
});

export async function adminList({ data }: { data: unknown }) {
  const input = listSchema.parse(data);
  const cfg = MANAGE_TABLES[input.table];
  let q = supabase.from(input.table).select("*");
  for (const [col, asc] of cfg.order as any) {
    q = q.order(col, { ascending: asc });
  }
  if (input.limit) q = q.limit(input.limit);
  const { data: rows, error } = await q;
  if (error) throw new Error(error.message);
  return (rows ?? []) as Row[];
}

const upsertSchema = z.object({
  table: z.enum(TABLE_NAMES),
  values: z.record(z.any()),
});

export async function adminUpsert({ data }: { data: unknown }) {
  const input = upsertSchema.parse(data);
  const cfg = MANAGE_TABLES[input.table];
  const idColumn = cfg.idColumn;
  const values = { ...input.values };
  const idValue = values[idColumn];

  if (idValue && idColumn !== "id") {
    const { data: row, error } = await supabase
      .from(input.table)
      .upsert(values as any, { onConflict: idColumn })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as Row;
  }

  if (idValue) {
    const { data: row, error } = await supabase
      .from(input.table)
      .update(values as any)
      .eq(idColumn as any, idValue)
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as Row;
  }

  delete values[idColumn];
  const { data: row, error } = await supabase
    .from(input.table)
    .insert(values as any)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return row as Row;
}

const deleteSchema = z.object({ table: z.enum(TABLE_NAMES), id: z.string().min(1) });

export async function adminDelete({ data }: { data: unknown }) {
  const input = deleteSchema.parse(data);
  const cfg = MANAGE_TABLES[input.table];
  const { error } = await supabase
    .from(input.table)
    .delete()
    .eq(cfg.idColumn as any, input.id);
  if (error) throw new Error(error.message);
  return { ok: true };
}

export async function adminSettings() {
  const { data, error } = await supabase.from("site_settings").select("key,value");
  if (error) throw new Error(error.message);
  return (data ?? []) as { key: string; value: any }[];
}

const saveSettingsSchema = z.object({
  entries: z.array(z.object({ key: z.string().min(1), value: z.any() })),
});

export async function adminSaveSettings({ data }: { data: unknown }) {
  const input = saveSettingsSchema.parse(data);
  const { error } = await supabase.from("site_settings").upsert(
    input.entries.map((e) => ({
      key: e.key,
      value: e.value,
      updated_at: new Date().toISOString(),
    })),
    { onConflict: "key" },
  );
  if (error) throw new Error(error.message);
  return { ok: true };
}

export async function adminStats() {
  const sb = supabase;
  const counters: Array<[string, any]> = [
    ["proposals", sb.from("proposals").select("id", { count: "exact", head: true })],
    ["works", sb.from("works").select("id", { count: "exact", head: true })],
    ["news", sb.from("news").select("id", { count: "exact", head: true })],
    ["events", sb.from("events").select("id", { count: "exact", head: true })],
    ["videos", sb.from("videos").select("id", { count: "exact", head: true })],
    ["galleries", sb.from("galleries").select("id", { count: "exact", head: true })],
    ["testimonials", sb.from("testimonials").select("id", { count: "exact", head: true })],
    ["contacts", sb.from("contacts").select("id", { count: "exact", head: true })],
    [
      "contacts_new",
      sb.from("contacts").select("id", { count: "exact", head: true }).eq("status", "new"),
    ],
    [
      "participation_forms",
      sb.from("participation_forms").select("id", { count: "exact", head: true }),
    ],
    [
      "participation_forms_new",
      sb
        .from("participation_forms")
        .select("id", { count: "exact", head: true })
        .eq("status", "new"),
    ],
  ];

  const results = await Promise.all(counters.map(([, q]) => q));
  const out: Record<string, number> = {};
  counters.forEach(([key], i) => {
    out[key] = results[i].count ?? 0;
  });

  const [{ data: recentContacts }, { data: recentParticipation }] = await Promise.all([
    sb.from("contacts").select("*").order("created_at", { ascending: false }).limit(5),
    sb.from("participation_forms").select("*").order("created_at", { ascending: false }).limit(5),
  ]);

  return {
    counts: out,
    recentContacts: recentContacts ?? [],
    recentParticipation: recentParticipation ?? [],
  };
}

const messageStatusSchema = z.object({
  table: z.enum(["contacts", "participation_forms"]),
  id: z.string().min(1),
  status: z.enum(["new", "viewed", "answered", "archived"]),
});

export async function adminUpdateMessageStatus({ data }: { data: unknown }) {
  const input = messageStatusSchema.parse(data);
  const { error } = await supabase
    .from(input.table)
    .update({ status: input.status })
    .eq("id", input.id);
  if (error) throw new Error(error.message);
  return { ok: true };
}
